import { FC, useEffect, useMemo, useState } from "react";
import classes from './studentTags.module.scss'
import { IStudentTag, studentService } from "../../../../entities/student";
import { Modal } from "../../../../shared/ui/modal";
import { MyCheckbox } from "../../../../shared/ui/myCheckbox/myCheckbox";
import { MyButton } from "../../../../shared/ui/button";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { SearchItems } from "../../../../features/searchItems";
import { useAppSelector } from "../../../../app/store/store";

interface IProps {
    studentId: number;
    targetUserId: number;
    tags?: IStudentTag[];
    setTags: (tags: IStudentTag[]) => void;
}

const EMPTY_TAGS: IStudentTag[] = []

const isSameIds = (firstIds: number[], secondIds: number[]): boolean => {
    return firstIds.length === secondIds.length &&
        firstIds.every((id, index) => id === secondIds[index])
}

export const StudentTags: FC<IProps> = ({studentId, targetUserId, tags, setTags}) => {

    const getTagName = (tag: IStudentTag) => tag.tag_name || `Тег #${tag.id}`
    const studentTags = tags ?? EMPTY_TAGS

    const [open, setOpen] = useState<boolean>(false)
    const [allTags, setAllTags] = useState<IStudentTag[]>([])
    const [allTagsSearch, setAllTagsSearch] = useState<Array<IStudentTag & {name: string}>>([])
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const {setIsLoading: setGlobalLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canViewStudentTags = hasFeature(my, FEATURES.STUDENTS.TAGS_STUDENT_LIST, targetUserId)
    const canListTags = hasFeature(my, FEATURES.STUDENTS.TAGS_LIST)
    const canAssignTags = hasFeature(my, FEATURES.STUDENTS.TAGS_ASSIGN, targetUserId)
    const canRemoveTags = hasFeature(my, FEATURES.STUDENTS.TAGS_REMOVE, targetUserId)
    const canManageTags = canListTags && (canAssignTags || canRemoveTags)

    useEffect(() => {
        const tagIds = studentTags.map(tag => tag.id)

        setSelectedTagIds(current => isSameIds(current, tagIds) ? current : tagIds)
    }, [studentTags])

    useEffect(() => {
        const loadStudentTags = async () => {
            try{
                setIsLoading(true)
                const studentTags = await studentService.getStudentTags(studentId)
                setTags(studentTags)
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении тегов студента', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        if(studentId && canViewStudentTags){
            loadStudentTags()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentId, canViewStudentTags])

    const sortedTags = useMemo(
        () => [...studentTags].sort((a, b) => a.tag_name.localeCompare(b.tag_name)),
        [studentTags]
    )

    const loadTags = async () => {
        try{
            setIsLoading(true)
            const tagsRes = await studentService.getTags()
            setAllTags(tagsRes)
            setAllTagsSearch(tagsRes.map(tag => ({...tag, name: getTagName(tag)})))
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении тегов студента', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onOpen = async () => {
        if(!canManageTags){
            return
        }
        setSelectedTagIds(studentTags.map(tag => tag.id))
        setOpen(true)
        await loadTags()
    }

    const onSelected = (tagId: number) => {
        return (selected: boolean) => {
            setSelectedTagIds(current =>
                selected
                    ? current.includes(tagId) ? current : [...current, tagId]
                    : current.filter(id => id !== tagId)
            )
        }
    }

    const onRemove = async (tagId: number) => {
        if(!canRemoveTags){
            return
        }
        try{
            setGlobalLoading(true)
            await studentService.removeTagFromStudent(studentId, tagId)
            setTags(studentTags.filter(tag => tag.id !== tagId))
            setGlobalMessage({message: 'Тег удален у студента', type: 'ok'})
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при удалении тега у студента', type: 'error'})
            }
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const onSave = async () => {
        const currentTagIds = studentTags.map(tag => tag.id)
        const tagIdsToAdd = selectedTagIds.filter(id => !currentTagIds.includes(id))
        const tagIdsToRemove = currentTagIds.filter(id => !selectedTagIds.includes(id))
        const allKnownTags = [...studentTags, ...allTags].reduce((acc, tag) => {
            if(!acc.find(item => item.id === tag.id)){
                acc.push(tag)
            }
            return acc
        }, [] as IStudentTag[])

        try{
            if(tagIdsToAdd.length > 0 && !canAssignTags){
                setGlobalMessage({message: 'Нет доступа на назначение тегов студенту', type: 'error'})
                return
            }
            if(tagIdsToRemove.length > 0 && !canRemoveTags){
                setGlobalMessage({message: 'Нет доступа на снятие тегов у студента', type: 'error'})
                return
            }
            setGlobalLoading(true)
            await Promise.all(tagIdsToAdd.map(tagId => studentService.addTagToStudent(studentId, tagId)))
            await Promise.all(tagIdsToRemove.map(tagId => studentService.removeTagFromStudent(studentId, tagId)))

            const nextTags = allKnownTags
                .filter(tag => selectedTagIds.includes(tag.id))
                .sort((a, b) => a.tag_name.localeCompare(b.tag_name))

            setTags(nextTags)
            setOpen(false)
            setGlobalMessage({message: 'Теги студента обновлены', type: 'ok'})
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при обновлении тегов студента', type: 'error'})
            }
        }
        finally{
            setGlobalLoading(false)
        }
    }

    if(!canViewStudentTags){
        return null
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <span className={classes.title}>
                    <svg className={classes.titleIcon} viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5V12L12 20L20 12L13 4H4V5Z" /><circle cx="8" cy="8" r="1" /></svg>
                    Теги студента
                </span>
                {canManageTags && <button className={classes.add} onClick={onOpen}>+</button>}
            </section>
            <section className={classes.tags}>
                {
                    sortedTags.length > 0
                        ?
                    sortedTags.map(tag => (
                        <section key={tag.id} className={classes.tag}>
                            <span>{getTagName(tag)}</span>
                            {canRemoveTags && (
                                <button className={classes.remove} onClick={() => onRemove(tag.id)}>
                                    x
                                </button>
                            )}
                        </section>
                    ))
                        :
                    <span className={classes.empty}>Теги пока не назначены</span>
                }
            </section>
            <Modal open={open} setOpen={setOpen}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Выберите теги студента</section>
                    <section className={classes.search}>
                        <SearchItems
                            placeholder="Введите название тега"
                            items={allTags.map(tag => ({...tag, name: getTagName(tag)}))}
                            setItems={setAllTagsSearch}
                        />
                    </section>
                    <section className={classes.modalList}>
                        {
                            isLoading
                                ?
                            <span className={classes.empty}>Загрузка...</span>
                                :
                            allTagsSearch.length > 0
                                ?
                            allTagsSearch
                                .slice()
                                .sort((a, b) => a.tag_name.localeCompare(b.tag_name))
                                .map(tag => (
                                    <section key={tag.id} className={classes.checkbox}>
                                        <MyCheckbox
                                            checked={selectedTagIds.includes(tag.id)}
                                            onSelected={onSelected(tag.id)}
                                            label={getTagName(tag)}
                                        />
                                    </section>
                                ))
                                :
                            <span className={classes.empty}>Список тегов пуст</span>
                        }
                    </section>
                    <section className={classes.actions}>
                        <MyButton onClick={onSave} isLoading={isLoading}>
                            Сохранить
                        </MyButton>
                    </section>
                </section>
            </Modal>
        </section>
    )
}
